import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { authStore } from "@/lib/auth";
import { api, ApiError } from "@/lib/api";
import { NotificationBell } from "./NotificationBell";
import { MobileNav } from "./MobileNav";

interface Props {
  children: ReactNode;
  title: string;
}

const NAV = [
  { to: "/counselor", labelKey: "counselor.nav.caseload", default: "Caseload", exact: true },
  { to: "/counselor/review", labelKey: "counselor.nav.review", default: "Review queue" },
];

export function CounselorLayout({ children, title }: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!user || !["counselor", "admin", "super_admin"].includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  async function logout() {
    const refreshToken = authStore.getRefreshToken();
    try {
      if (refreshToken)
        await api.post("/v1/auth/logout", { refreshToken }, { skipAuth: true });
    } catch (e) {
      if (!(e instanceof ApiError)) throw e;
    } finally {
      authStore.clear();
      navigate("/login", { replace: true });
    }
  }

  return (
    <div className="min-h-screen flex bg-neutral-50">
      <aside className="hidden md:flex w-60 bg-white border-r border-neutral-200 flex-col">
        <div className="px-4 py-5 border-b border-neutral-200">
          <h1 className="text-lg font-semibold">Balans Buddy</h1>
          <p className="text-xs text-neutral-500 mt-0.5">{t("counselor.subtitle", "Counselor")}</p>
        </div>
        <nav className="flex-1 px-2 py-4 space-y-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              className={({ isActive }) =>
                `block px-3 py-2 text-sm rounded-lg transition-colors ${
                  isActive
                    ? "bg-brand-50 text-brand-700 font-medium"
                    : "text-neutral-700 hover:bg-neutral-100"
                }`
              }
            >
              {t(item.labelKey, item.default)}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-neutral-200">
          <p className="text-xs text-neutral-500 mb-1 truncate">{user.email}</p>
          <p className="text-xs text-neutral-400 mb-3">{user.role}</p>
          <button
            onClick={logout}
            className="block w-full text-left text-xs text-neutral-600 hover:text-neutral-900"
          >
            {t("logout", "Uitloggen")}
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-auto">
        <header className="bg-white border-b border-neutral-200 px-4 md:px-6 py-3 md:py-4 flex items-center gap-3">
          <MobileNav items={NAV} userEmail={user.email} role="Counselor" />
          <h2 className="text-lg md:text-xl font-semibold text-neutral-900 flex-1 truncate">{title}</h2>
          <NotificationBell />
        </header>
        <div className="p-6">{children}</div>
      </main>
    </div>
  );
}
